import React, { useEffect, useRef, useMemo } from 'react';
import { DialogueLine, EntryTake } from '../../types';
import { MessageSquare, Lock } from 'lucide-react';

interface DialogueLinesPanelProps {
  dialogueLines: DialogueLine[];
  currentTime: number;
  duration: number;
  selectedLineId: string | null;
  selectedLineIds: Set<string>;
  isPlaying?: boolean;
  isRecording?: boolean;
  videoHeight?: number | null;
  linesListRef: React.RefObject<HTMLDivElement | null>;
  takes?: EntryTake[];
  currentPerformer?: string;
  partnerName?: string;
  onSelectLine?: (lineId: string, focus: 'user' | 'orig') => void;
  onToggleSelectLine: (lineId: string, focus: 'user' | 'orig') => void;
  onSelectAllLines?: () => void;
  onDeselectAllLines?: () => void;
  onSeek: (time: number) => void;
  onAuditionSegment?: (start: number, end: number) => void;
}

export const DialogueLinesPanel: React.FC<DialogueLinesPanelProps> = ({
  dialogueLines,
  currentTime,
  duration,
  selectedLineId,
  selectedLineIds,
  isPlaying = false,
  isRecording = false,
  videoHeight,
  linesListRef,
  takes = [],
  currentPerformer,
  partnerName,
  onSelectLine,
  onToggleSelectLine,
  onSelectAllLines,
  onDeselectAllLines,
  onSeek,
  onAuditionSegment
}) => {
  const lastActiveLineIdRef = useRef<string | null>(null);

  const lockedLineMap = useMemo(() => {
    const map = new Map<string, EntryTake>();
    if (!takes) return map;
    for (const t of takes) {
      if (t.isActive === false) continue;
      const isLocked = Boolean(
        t.isLocked ||
        t.isPartnerTake ||
        (t.performerName && currentPerformer && t.performerName.toLowerCase() !== currentPerformer.toLowerCase())
      );
      if (isLocked) {
        if (t.lineIds && t.lineIds.length > 0) {
          for (const lid of t.lineIds) {
            map.set(lid, t);
          }
        } else if (t.id) {
          map.set(t.id, t);
        }
      }
    }
    return map;
  }, [takes, currentPerformer]);

  // Memoized word timings for every line: Whisper words if present, synthesized proportional words for older clips
  const lineWordsMap = useMemo(() => {
    const map = new Map<string, Array<{ word: string; start: number; end: number }>>();
    for (const line of dialogueLines) {
      const start = line.start ?? line.startTime ?? 0;
      const end = line.end ?? line.endTime ?? duration;
      if (line.words && line.words.length > 0) {
        const cleaned = line.words
          .map((w) => ({
            word: (w.word || '').trim(),
            start: w.start,
            end: w.end
          }))
          .filter((w) => Boolean(w.word));

        // Anti-smear guard: clamp words where Whisper dragged the start across leading silence
        const clamped = cleaned.map((w, wIdx) => {
          const cleanWord = w.word.replace(/[^a-zA-Z0-9]/g, '');
          const len = Math.max(1, cleanWord.length);
          const maxDur = Math.min(2.2, Math.max(0.55, len * 0.16));
          if (w.end - w.start > maxDur) {
            const prevEnd = wIdx > 0 ? cleaned[wIdx - 1].end : w.start;
            const tightenedStart = Math.max(w.start, Math.max(prevEnd, parseFloat((w.end - maxDur).toFixed(2))));
            return { ...w, start: tightenedStart };
          }
          return w;
        });

        map.set(line.id, clamped);
      } else {
        const text = (line.text || line.originalText || '').trim();
        if (!text) {
          map.set(line.id, []);
          continue;
        }
        const rawWords = text.split(/\s+/).filter(Boolean);
        const totalChars = rawWords.reduce((sum, w) => sum + Math.max(1, w.length), 0);
        const lineDur = Math.max(0.2, end - start);
        let curStart = start;
        const synWords = rawWords.map((word) => {
          const dur = (Math.max(1, word.length) / totalChars) * lineDur;
          const wordEnd = curStart + dur;
          const item = { word, start: curStart, end: wordEnd };
          curStart = wordEnd;
          return item;
        });
        map.set(line.id, synWords);
      }
    }
    return map;
  }, [dialogueLines, duration]);

  // Gentle auto-roll for Dialogue Lines panel: keep active line comfortably in view
  // Accounts for dynamic text size expansion (0.88rem -> 1.16rem) and wrapping to 2+ lines
  useEffect(() => {
    if (!isPlaying || !linesListRef.current || dialogueLines.length === 0) return;
    const activeLine = dialogueLines.find((l) => {
      const start = l.start ?? l.startTime ?? 0;
      const end = l.end ?? l.endTime ?? duration;
      return currentTime >= start && currentTime <= end;
    });

    if (!activeLine) {
      lastActiveLineIdRef.current = null;
      return;
    }

    const isNewLine = activeLine.id !== lastActiveLineIdRef.current;
    if (isNewLine) {
      lastActiveLineIdRef.current = activeLine.id;
    }

    const checkAndScroll = () => {
      const container = linesListRef.current;
      if (!container) return;
      const rowEl = container.querySelector(`[data-line-id="${activeLine.id}"]`) as HTMLElement | null;
      if (!rowEl) return;

      const containerRect = container.getBoundingClientRect();
      const rowRect = rowEl.getBoundingClientRect();
      const containerHeight = container.clientHeight;

      const rowTopInView = rowRect.top - containerRect.top;
      const rowBottomInView = rowRect.bottom - containerRect.top;

      // Generous bottom margin (100px) ensures even a 2 or 3-line expanded card with bottom progress bar never gets clipped
      const bottomMargin = 100;
      const topMargin = 16;

      if (rowBottomInView > containerHeight - (isNewLine ? bottomMargin : 32)) {
        const scrollDelta = rowBottomInView - (containerHeight - bottomMargin);
        container.scrollBy({ top: scrollDelta + 16, behavior: 'smooth' });
      } else if (rowTopInView < topMargin && container.scrollTop > 0) {
        const scrollDelta = rowTopInView - topMargin;
        container.scrollBy({ top: scrollDelta - 10, behavior: 'smooth' });
      }
    };

    if (isNewLine) {
      requestAnimationFrame(checkAndScroll);
      const timer = setTimeout(checkAndScroll, 80);
      return () => clearTimeout(timer);
    } else {
      checkAndScroll();
    }
  }, [currentTime, dialogueLines, duration, linesListRef, isPlaying]);
  return (
    <div
      className="studio-lines-panel"
      style={{
        height: '480px',
        maxHeight: '480px'
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '0.5rem',
          marginBottom: '0.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <MessageSquare size={16} color="var(--accent-purple)" />
          <span style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '6px' }}>
            Select Auras to Sync
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              padding: '2px 7px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 700,
              background: selectedLineIds.size > 0 ? 'rgba(139, 92, 246, 0.25)' : 'rgba(255, 255, 255, 0.06)',
              color: selectedLineIds.size > 0 ? '#c084fc' : 'var(--text-muted)',
              border: selectedLineIds.size > 0 ? '1px solid rgba(168, 85, 247, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)'
            }}
            title={lockedLineMap.size > 0
              ? `${selectedLineIds.size} of ${dialogueLines.length - lockedLineMap.size} available lines selected (${lockedLineMap.size} locked by partner)`
              : `${selectedLineIds.size} of ${dialogueLines.length} lines selected to record`}
          >
            {selectedLineIds.size}/{Math.max(0, dialogueLines.length - lockedLineMap.size)} Selected
          </span>
          {onSelectAllLines && (
            <button
              type="button"
              onClick={onSelectAllLines}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#e2e8f0',
                fontSize: '10px',
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title="Select all lines to record"
            >
              All
            </button>
          )}
          {onDeselectAllLines && selectedLineIds.size > 0 && (
            <button
              type="button"
              onClick={onDeselectAllLines}
              style={{
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'var(--text-muted)',
                fontSize: '10px',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title="Clear all selections"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Container with Lines */}
      <div
        ref={linesListRef}
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.4rem',
          paddingRight: '4px',
          paddingBottom: '6.5rem',
          scrollbarWidth: 'thin'
        }}
      >
        {dialogueLines.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <MessageSquare size={24} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
            <div style={{ fontSize: '0.85rem', color: '#ffffff' }}>No Auras available</div>
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem' }}>
              Auras will appear as their whisper can be heard.
            </p>
          </div>
        ) : (
          dialogueLines.map((line, idx) => {
            const start = line.start ?? line.startTime ?? 0;
            const end = line.end ?? line.endTime ?? duration;
            const partnerTake = lockedLineMap.get(line.id);
            const isLineLocked = Boolean(partnerTake);
            const isLineActive = (isPlaying || isRecording) && currentTime >= start && currentTime <= end;
            const isSelectedForDub = !isLineLocked && selectedLineIds.has(line.id);
            const isInspected = selectedLineId === line.id;
            const shouldExpand = Boolean(isLineActive);
            const progressPct = isLineActive
              ? Math.min(100, Math.max(0, ((currentTime - start) / Math.max(0.05, end - start)) * 100))
              : 0;
            const words = lineWordsMap.get(line.id) || [];

            return (
              <div
                key={line.id || idx}
                data-line-id={line.id}
                onClick={() => {
                  if (isLineLocked) {
                    onSeek(start);
                    if (onSelectLine) onSelectLine(line.id, 'user');
                  } else {
                    onToggleSelectLine(line.id, 'orig');
                    onSeek(start);
                  }
                }}
                style={{
                  position: 'relative',
                  flexShrink: 0,
                  minHeight: 'fit-content',
                  padding: shouldExpand ? '0.85rem 0.95rem 1.15rem 0.95rem' : '0.65rem 0.85rem 0.75rem 0.85rem',
                  borderRadius: '8px',
                  cursor: isLineLocked ? 'default' : 'pointer',
                  opacity: isLineLocked ? 0.62 : 1,
                  transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
                  background: isLineLocked
                    ? (isLineActive ? 'linear-gradient(135deg, rgba(30, 58, 138, 0.7) 0%, rgba(15, 23, 42, 0.9) 100%)' : 'rgba(15, 23, 42, 0.4)')
                    : (isLineActive
                      ? isSelectedForDub
                        ? 'linear-gradient(135deg, rgba(126, 34, 206, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)'
                        : 'linear-gradient(135deg, rgba(30, 58, 138, 0.92) 0%, rgba(15, 23, 42, 0.98) 100%)'
                      : isSelectedForDub
                      ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.32) 0%, rgba(88, 28, 135, 0.28) 100%)'
                      : 'rgba(15, 23, 42, 0.55)'),
                  border: isLineActive
                    ? '1.5px solid #38bdf8'
                    : isLineLocked
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : isSelectedForDub
                    ? '1.5px solid rgba(168, 85, 247, 0.8)'
                    : isInspected
                    ? '1px solid rgba(168, 85, 247, 0.4)'
                    : '1px solid rgba(255, 255, 255, 0.08)',
                  borderLeft: isLineActive
                    ? '5px solid #38bdf8'
                    : isLineLocked
                    ? '4px solid rgba(245, 158, 11, 0.65)'
                    : isSelectedForDub
                    ? '4px solid #c084fc'
                    : isInspected
                    ? '4px solid rgba(168, 85, 247, 0.5)'
                    : '4px solid transparent',
                  boxShadow: isLineActive
                    ? '0 0 20px rgba(56, 189, 248, 0.4), 0 4px 12px rgba(0, 0, 0, 0.6)'
                    : isSelectedForDub
                    ? '0 0 14px rgba(168, 85, 247, 0.28), inset 0 0 12px rgba(147, 51, 234, 0.12)'
                    : 'none',
                  zIndex: isLineActive ? 5 : 1
                }}
                title={isLineLocked
                  ? `Duet Voice by @${partnerTake?.performerName || partnerName || 'Partner'} (Locked) • Cannot record over`
                  : `Line (${start.toFixed(1)}s - ${end.toFixed(1)}s) • Click to ${isSelectedForDub ? 'deselect' : 'select to record'}`}
              >
                {/* Real-time playback guide progress line across bottom of active row */}
                {isLineActive && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #38bdf8 0%, #a855f7 100%)',
                      width: `${progressPct}%`,
                      boxShadow: '0 0 10px rgba(56, 189, 248, 0.95)',
                      transition: 'width 0.05s linear',
                      pointerEvents: 'none',
                      borderRadius: '0 0 8px 8px'
                    }}
                  />
                )}

                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                  {/* Spoken Dialogue Text taking full primary space */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: shouldExpand ? '1.16rem' : '0.88rem',
                      fontWeight: isLineActive ? 800 : (isSelectedForDub || isInspected ? 700 : 600),
                      color: isLineLocked
                        ? '#94a3b8'
                        : (isLineActive ? '#ffffff' : (isSelectedForDub ? '#f1f5f9' : '#cbd5e1')),
                      lineHeight: shouldExpand ? 1.45 : 1.35,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      textShadow: isLineActive
                        ? '0 2px 4px rgba(0, 0, 0, 0.9)'
                        : (isSelectedForDub ? '0 1px 4px rgba(0, 0, 0, 0.6)' : 'none'),
                      transition: 'color 0.15s ease'
                    }}
                  >
                    {isLineActive && words.length > 0 ? (
                      <span>
                        {words.map((w, wIdx) => {
                          const isWordActive = currentTime >= w.start && currentTime <= w.end;
                          const isWordPast = currentTime > w.end;
                          return (
                            <span
                              key={wIdx}
                              style={{
                                color: isWordActive
                                  ? '#fef08a'
                                  : (isWordPast ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'),
                                textShadow: isWordActive
                                  ? '0 0 10px rgba(250, 204, 21, 0.9), 0 0 3px #eab308'
                                  : (isWordPast ? '0 1px 3px rgba(0, 0, 0, 0.8)' : undefined),
                                fontWeight: isWordActive ? 900 : (isWordPast ? 700 : 600),
                                transform: isWordActive ? 'scale(1.04)' : 'none',
                                transformOrigin: 'left center',
                                transition: 'color 60ms linear, text-shadow 60ms linear, transform 60ms ease',
                                display: 'inline-block',
                                marginRight: '0.28em'
                              }}
                            >
                              {w.word}
                            </span>
                          );
                        })}
                      </span>
                    ) : (
                      <span>
                        {line.text || line.originalText || `Dialogue Sentence`}
                      </span>
                    )}
                  </div>

                  {/* Right side: Selected badge / Partner Locked Badge + Timestamp */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginTop: '2px' }}>
                    {isLineLocked ? (
                      <span
                        style={{
                          padding: '2px 7px',
                          borderRadius: '4px',
                          fontSize: '9.5px',
                          fontWeight: 800,
                          letterSpacing: '0.03em',
                          background: 'rgba(245, 158, 11, 0.18)',
                          color: '#fde68a',
                          boxShadow: '0 0 8px rgba(245, 158, 11, 0.25)',
                          border: '1px solid rgba(245, 158, 11, 0.4)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0
                        }}
                        title={`Locked Duet Take by @${partnerTake?.performerName || partnerName || 'Partner'}`}
                      >
                        <Lock size={10} />
                        @{partnerTake?.performerName || partnerName || 'Partner'}
                      </span>
                    ) : (
                      isSelectedForDub && (
                        <span
                          style={{
                            padding: '2px 7px',
                            borderRadius: '4px',
                            fontSize: '9.5px',
                            fontWeight: 800,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                            color: '#ffffff',
                            boxShadow: '0 0 10px rgba(168, 85, 247, 0.55)',
                            border: '1px solid rgba(216, 180, 254, 0.6)',
                            flexShrink: 0
                          }}
                        >
                          Selected
                        </span>
                      )
                    )}

                    <span
                      style={{
                        fontSize: isLineActive ? '11px' : '10px',
                        fontFamily: 'var(--font-mono)',
                        color: isLineActive ? '#93c5fd' : (isSelectedForDub ? '#c084fc' : 'var(--text-muted)'),
                        fontWeight: isLineActive || isSelectedForDub ? 700 : 500
                      }}
                    >
                      {start.toFixed(1)}s
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
